import { ButtonBase, CSSObject, Divider, SelectChangeEvent, styled, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  transactionFlowsSelector,
  useGetConfigurationQuery,
  useGetVirtualAccountQuery,
} from '@/api/v1';
import USFlag from '@/assets/flags/us.svg?react';
import { AmountInputWithFloatingCurrency } from '@/components/molecules/AmountInputWithFloatingCurrency';
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown';
import {
  EmptyFundingMethodChip,
  FundingMethodChip,
} from '@/components/molecules/FundingMethodChip';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { PaymentFlowModalMessage } from '@/components/molecules/PaymentFlowModalMessage';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { FiatAccountDetailsContent } from '@/components/organisms/FiatAccountDetailsContent';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import {
  DepositFlowAction,
  DepositFlowState,
  fromAmountChanged,
  fundingMethodChanged,
  transactionFlowChanged,
} from '@/Screens/Dashboard/Flows/DepositFlow/reducer';
import { CurrencyCodeType, DashboardOperation, PaymentRailType } from '@/types/currency';
import { isPaymentRailType } from '@/types/currency.ext';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import { isStablecoin } from '@/utility/isStablecoin';
import { OutletReducer } from '@/utility/reducers';

enum SubmitError {
  REQUIRED_AMOUNT = 'REQUIRED_AMOUNT',
  REQUIRED_FUNDING_METHOD = 'REQUIRED_FUNDING_METHOD',
}

interface Props {
  onClose(): void;
}

export const DepositAmount: React.FC<Props> = (props: Props): React.ReactElement => {
  const [state, dispatch] = useOutletContext<OutletReducer<DepositFlowState, DepositFlowAction>>();
  const [error, setError] = React.useState<SubmitError | null>(null);
  const navigate = useNavigate();

  const inputContainerRef = React.useRef<HTMLDivElement>(null);
  const tr = useTranslator();
  const customer = useCustomer();

  const { data: flows = [] } = useSelector(transactionFlowsSelector('DEPOSIT'));
  const {
    fromAmount = null,
    fromCurrency = CurrencyCodeType.UNSET,
    fundingMethods,
    selectedFundingMethod,
  } = state;
  const { onClose } = props;

  const { data: paymentInstructions } = useGetVirtualAccountQuery(
    {
      beneficiaryId: customer.beneficiaryId ?? '',
      paymentRail: selectedFundingMethod,
    },
    {
      skip:
        !customer.beneficiaryId ||
        (selectedFundingMethod !== PaymentRailType.ACH &&
          selectedFundingMethod !== PaymentRailType.Wire),
    },
  );

  const information = React.useMemo((): React.ReactElement | null => {
    if (fromCurrency === CurrencyCodeType.UNSET || isStablecoin(fromCurrency)) {
      return null;
    }

    return (
      <>
        <Typography component="span" key="text" className="inherit">
          {tr('CURRENCY_CONVERT_TO_DIGITAL_DOLLARS', fromCurrency)}{' '}
        </Typography>
      </>
    );
  }, [fromCurrency, tr]);

  const displayFundingMethodValue = React.useCallback(
    (value: unknown): React.ReactNode => {
      if (typeof value === 'string' && value && isPaymentRailType(value)) {
        return <FundingMethodChip fundingMethod={value} />;
      }
      return <EmptyFundingMethodChip label={tr('FUNDING_METHOD')} />;
    },
    [tr],
  );

  const onContinue = React.useCallback((): void => {
    if (!selectedFundingMethod) {
      setError(SubmitError.REQUIRED_FUNDING_METHOD);
      return;
    }

    if (!fromAmount) {
      setError(SubmitError.REQUIRED_AMOUNT);
      return;
    }

    navigate('../review', { relative: 'route' });
  }, [fromAmount, selectedFundingMethod, setError, navigate]);

  const focusInput = React.useCallback((): void => {
    const { current: container } = inputContainerRef;
    if (!container) {
      return;
    }

    setTimeout((): void => {
      const input = container.querySelector('input');
      input?.focus();
    }, 0);
  }, []);

  const updateSelectedCurrency = React.useCallback(
    (value: string): void => {
      const flow = flows.find(
        (operation: DashboardOperation): boolean => operation.currency === value,
      );
      if (!flow) {
        throw new Error(`no flow found for currency: ${value}`);
      }

      dispatch(transactionFlowChanged(flow));
      dispatch(fromAmountChanged(null));

      focusInput();
    },
    [dispatch, flows, focusInput],
  );

  const onCurrencyChange = React.useCallback(
    (value: string): void => {
      updateSelectedCurrency(value);
    },
    [updateSelectedCurrency],
  );

  const fromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows.map((operation: DashboardOperation): CurrencyCodeType => operation.currency),
    [flows],
  );

  const message = React.useMemo(
    (): ReadonlyArray<string | React.ReactElement> => (information ? [information] : []),
    [information],
  );

  const handleAmountChange = React.useCallback(
    (amount: number | null): void => {
      if (error && error === SubmitError.REQUIRED_AMOUNT) {
        setError(null);
      }

      dispatch(fromAmountChanged(amount));
    },
    [error, setError, dispatch],
  );

  const onFundingMethodChange = React.useCallback(
    (event: SelectChangeEvent<unknown>, _: React.ReactNode): void => {
      if (error && error === SubmitError.REQUIRED_FUNDING_METHOD) {
        setError(null);
      }

      const { value } = event.target;
      if (isPaymentRailType(value)) {
        dispatch(fundingMethodChanged(value));
      }
    },
    [error, setError, dispatch],
  );

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
  ]);

  return (
    <ApplicationModal title={tr('CHOOSE_DEPOSIT_AMOUNT')} onClose={onClose}>
      <Box display="flex" alignItems="center" gap={1} height={34}>
        <CurrencyDropdown
          value={fromCurrency}
          currencies={fromCurrencies}
          onChange={onCurrencyChange}
        />
        {match(selectedFundingMethod)
          .with(PaymentRailType.ACH, (): React.ReactNode => null)
          .with(PaymentRailType.Wire, (): React.ReactNode => null)
          .otherwise((): React.ReactNode => {
            if (fundingMethods.length <= 1) {
              return (
                <FundingMethodItem display="flex" alignItems="center">
                  <FundingMethodChip fundingMethod={selectedFundingMethod} />
                </FundingMethodItem>
              );
            } else {
              return (
                <FundingMethodSelect
                  displayEmpty={true}
                  renderValue={displayFundingMethodValue}
                  value={selectedFundingMethod}
                  error={error === SubmitError.REQUIRED_FUNDING_METHOD}
                  onChange={onFundingMethodChange}>
                  {fundingMethods.map(
                    (fundingMethod: PaymentRailType): React.ReactElement => (
                      <FundingMethodMenuItem key={fundingMethod} value={fundingMethod}>
                        <FundingMethodChip fundingMethod={fundingMethod} />
                      </FundingMethodMenuItem>
                    ),
                  )}
                </FundingMethodSelect>
              );
            }
          })}
      </Box>

      {(selectedFundingMethod === PaymentRailType.ACH ||
        selectedFundingMethod === PaymentRailType.Wire) &&
      paymentInstructions ? (
        <>
          <Box sx={styles.fiatAccountContainer}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="h7">{tr('DEPOSIT_FIAT_ACCOUNT_MODAL_TITLE')}</Typography>
              <Typography variant="body2" color="custom.LightGray">
                {tr('DEPOSIT_FIAT_ACCOUNT_MODAL_DESCRIPTION')}
              </Typography>
            </Box>
            <Box my={4}>
              <Divider color="custom.DividerMed" />
            </Box>
            <Box>
              <Typography variant="h7">{tr('ACCOUNT_DETAILS')}</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <USFlag width={16} height={16} />
                <Typography variant="body2">
                  <Typography variant="inherit" component="span">
                    {tr('US_DOLLAR')}
                  </Typography>
                  <Typography variant="inherit" component="span">
                    {' '}
                  </Typography>
                  <Typography variant="inherit" component="span" color="custom.LightGray">
                    {tr('ONLY_FOR_DOMESTIC_ACH_AND_WIRES')}
                  </Typography>
                </Typography>
              </Box>
            </Box>
            <Box my={4}>
              <Divider color="custom.DividerMed" />
            </Box>
            <FiatAccountDetailsContent paymentInstructions={paymentInstructions} />
          </Box>
          <Box mt={5}>
            <ButtonBase role="button" sx={[styles.continueButton]} onClick={onClose}>
              <Typography
                fontSize={16}
                fontWeight={400}
                lineHeight="24px"
                color="primary.contrastText">
                {tr('CLOSE')}
              </Typography>
            </ButtonBase>
          </Box>
        </>
      ) : (
        <>
          <Box ref={inputContainerRef}>
            <AmountInputWithFloatingCurrency
              currency={fromCurrency}
              value={fromAmount}
              onChange={handleAmountChange}
            />
          </Box>
          {shouldNotAllowOperation ? (
            <OutsideBRLMarketHoursAlert />
          ) : (
            <PaymentFlowModalMessage type="info" message={message} />
          )}
          <Box mt={5}>
            <Button
              variant="contained"
              sx={styles.continueButton}
              onClick={onContinue}
              disabled={shouldNotAllowOperation}>
              {tr('CONTINUE')}
            </Button>
          </Box>
          <Box mt={2} display="flex" justifyContent="center">
            {error ? (
              <Typography variant="body2" color="error">
                {tr(error)}
              </Typography>
            ) : null}
          </Box>
        </>
      )}
    </ApplicationModal>
  );
};
const FundingMethodMenuItem = styled(MenuItem)({
  height: 40,
  paddingLeft: 8,
  paddingRight: 16,
});

const FundingMethodSelect = styled(Select)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    backgroundColor: theme.palette.background.paper,
    height: 32,
    '& .MuiInputBase-input': {
      position: 'relative',
      paddingLeft: '8px!important',
      paddingRight: '35px!important',
      paddingTop: 0,
      width: '100%',
      height: '100%',
    },
    '& .MuiSelect-icon': {
      cursor: 'pointer',
      right: 8,
    },
  }),
);

const FundingMethodItem = styled(Box)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    backgroundColor: theme.palette.custom.InputsBackground,
    height: 32,
    border: `1px solid ${theme.palette.custom.DividerLight}`,
    borderRadius: 24,
    paddingLeft: '8px!important',
    paddingRight: '8px!important',
  }),
);

const styles: Record<string, SystemStyleObject> = {
  continueButton: {
    width: '100%',
    height: 56,
    borderRadius: 10,
    backgroundColor: 'custom.BlackLightBackground',
  },
  fiatAccountContainer: {
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 4,
    px: 5,
    py: 4,
    mt: 3,
  },
};
